import { getLabelFromStorage, consoleEvents } from '@pb/utils'
import { AxiosError } from 'axios'
import { pathOr } from 'ramda'

import { PAYMENT_PROVIDERS } from 'App/constants'
import { ERROR_CO_PURCHASE_SYSTEM } from 'constants/optimizely'
import {
  CHECKOUT_ERROR_CODES,
  CHECKOUT_TRACKING_SECTIONS,
  MODAL_TRIGGERING_ERROR_CODES,
  SHOW_NOTIFICATION_TIME,
  TECHNICAL_ERROR_LABELS,
  ERROR_TYPES,
  CHECKOUT_TRACKING_ERROR_TYPES,
} from 'features/checkout/constants'
import { fetchContextSuccess, setError, setOutOfStockItems } from 'features/checkout/duck/actions'
import { normalizeContext } from 'features/checkout/duck/normalize'
import checkoutSelectors from 'features/checkout/duck/selectors'
import { fetchPaymentModes } from 'features/checkout/requests'
import { showNotification } from 'features/notifications/duck/actions'
import { setOrderTotals } from 'features/orderTotals/duck/actions'
import getErrorData from 'helpers/getErrorData'
import { trackOptimizelyEvent } from 'helpers/optimizely'
import { ThunkResult } from 'redux/types'

import { trackError } from '../track'

export const handleProceedError =
  (error: Error): ThunkResult<void> =>
  async (dispatch, getState) => {
    const state = getState()
    const paymentModeData = checkoutSelectors.getPaymentModesSettings(state)
    const { isTechnicalError, status, code } = getErrorData(error)
    const generalErrorMessage = getLabelFromStorage(TECHNICAL_ERROR_LABELS.general)
    const responseData = (error as AxiosError)?.response?.data

    try {
      const { paymentModeList } = await fetchPaymentModes()
      if (responseData?.contextData) {
        responseData.contextData.payment.paymentModeList = paymentModeList.map((mode) => ({
          ...paymentModeData[mode.code],
          ...mode,
        }))
      }
    } catch (err) {
      consoleEvents.error('Failed to fetch payment options', err)
    }

    if (responseData?.contextData) {
      const normalizedContext = normalizeContext(responseData.contextData)
      dispatch(fetchContextSuccess(normalizedContext))
      dispatch(setOrderTotals(responseData.contextData.orderTotals))
    }

    if (code === PAYMENT_PROVIDERS.KLARNA) {
      return
    }
    if (status === 102) {
      return
    }
    if (isTechnicalError) {
      dispatch(
        showNotification({
          content: generalErrorMessage,
          type: 'error',
          timespan: SHOW_NOTIFICATION_TIME,
        }),
      )
      trackOptimizelyEvent(ERROR_CO_PURCHASE_SYSTEM)
      dispatch(
        trackError({
          section: CHECKOUT_TRACKING_SECTIONS.completePurchase,
          message: generalErrorMessage,
          id: code,
          errorType: CHECKOUT_TRACKING_ERROR_TYPES.system,
        }),
      )
    } else {
      const { errorCode, errorType, header, message } = pathOr(
        {} as any,
        ['response', 'data', 'orderPlacementError'],
        error,
      )
      const isModalTriggeringError = MODAL_TRIGGERING_ERROR_CODES.some(
        (triggeringCode) => errorCode === triggeringCode,
      )
      const generalNoActionErrorMessage =
        errorType === ERROR_TYPES.payment
          ? getLabelFromStorage(message)
          : getLabelFromStorage(TECHNICAL_ERROR_LABELS.generalNoAction)
      const isSectionOpeningError = errorType && errorType !== 'GENERIC'

      if (errorCode === CHECKOUT_ERROR_CODES.insufficientStockLevel) {
        const outOfStockItems = responseData?.oosItems || []
        dispatch(setOutOfStockItems(outOfStockItems))
        trackOptimizelyEvent(ERROR_CO_PURCHASE_SYSTEM)
        dispatch(
          trackError({
            section: CHECKOUT_TRACKING_SECTIONS.completePurchase,
            message: getLabelFromStorage('checkout.oos.some.items'),
            id: errorCode,
            errorType: CHECKOUT_TRACKING_ERROR_TYPES.system,
          }),
        )
        return
      }

      if (isModalTriggeringError) {
        dispatch(
          setError({
            code: errorCode,
            type: errorType,
            message: generalNoActionErrorMessage,
            header: getLabelFromStorage(header),
          }),
        )
        trackOptimizelyEvent(ERROR_CO_PURCHASE_SYSTEM)
        dispatch(
          trackError({
            section: CHECKOUT_TRACKING_SECTIONS.completePurchase,
            message: generalNoActionErrorMessage,
            id: errorCode,
            errorType: CHECKOUT_TRACKING_ERROR_TYPES.system,
          }),
        )
        return
      }
      if (isSectionOpeningError) {
        if (errorType === ERROR_TYPES.payment) {
          dispatch(
            setError({
              code: errorCode,
              section: errorType,
              type: errorType,
              message: generalNoActionErrorMessage,
            }),
          )
        } else {
          dispatch(setError({ code: errorCode, section: errorType }))
        }
        return
      }
      if (errorCode === CHECKOUT_ERROR_CODES.noEntriesOnCartOOS) {
        const outOfStockItems = responseData?.oosItems || []
        dispatch(setOutOfStockItems(outOfStockItems))
        dispatch(setError({ code: errorCode }))
        trackOptimizelyEvent(ERROR_CO_PURCHASE_SYSTEM)
        dispatch(
          trackError({
            section: CHECKOUT_TRACKING_SECTIONS.completePurchase,
            message: getLabelFromStorage('checkout.oos.all.items'),
            id: errorCode,
            errorType: CHECKOUT_TRACKING_ERROR_TYPES.system,
          }),
        )
        return
      }

      const generalErrorCodes = [
        CHECKOUT_ERROR_CODES.nullCart,
        CHECKOUT_ERROR_CODES.noEntriesOnCart,
      ]

      const errorLabelKey = generalErrorCodes.includes(errorCode)
        ? TECHNICAL_ERROR_LABELS.generalNoAction
        : TECHNICAL_ERROR_LABELS.proceedGeneric

      dispatch(
        showNotification({
          content: getLabelFromStorage(message || errorLabelKey),
          type: 'error',
          timespan: SHOW_NOTIFICATION_TIME,
        }),
      )
      trackOptimizelyEvent(ERROR_CO_PURCHASE_SYSTEM)
      dispatch(
        trackError({
          section: CHECKOUT_TRACKING_SECTIONS.completePurchase,
          message: generalErrorMessage || `${status}`,
          id: errorCode || `${status}`,
          errorType: CHECKOUT_TRACKING_ERROR_TYPES.system,
        }),
      )
    }
  }
